import * as React from 'react';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/react-recompose/dist/Recompose.cjs.js' implicitly has an 'any' type.
import {withHandlers, compose} from 'react-recompose';
import {identity, noop} from 'lodash';

import {syncPropsToState} from '@packages/utilities/component';
import {styled, Z_INDEXES} from '@packages/utilities/styled';
import {emptyArray} from '@packages/utilities/fp/utils';

import {FlexRow, FlexColumn} from '@packages/systems/design-system/Layout';
import ListHeader from './ListHeader';
import {FinePrint} from '@packages/systems/design-system/Text';
import {BaseButton as Button} from '@packages/systems/design-system/Button';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const EmptyListItem = styled(FlexRow)(
  ({theme: {colors, gutters, control}, hasControls}) => ({
    boxSizing: 'border-box',
    height: control.heightLarge,
    background: colors.backgroundDeepAlt,
    paddingLeft: gutters.x8,
    paddingRight: gutters.x8,
    border: `${control.borderWidth}px solid ${colors.borderDeep}`,
    marginTop: hasControls && -1,
  })
);

const ListItem = styled(ListHeader)(
  ({theme: {gutters, control, colors}, first, last}) => ({
    flexShrink: 0,
    borderRadius: `${first ? control.borderRadius : 0}px 0px ${
      last ? control.borderRadius : 0
    }px ${last ? control.borderRadius : 0}px`,
    borderColor: colors.borderDeep,
    paddingLeft: gutters.x8,
    paddingRight: gutters.x8,
    marginTop: -1,
  }),
  {
    hover: ({theme: {colors}}) => ({
      background: colors.backgroundDeep,
    }),
  }
);

const StyledButton = styled(Button)(
  ({theme: {control}, first, last}) => ({
    borderRadius: 0,
    height: control.height,
    width: control.height,
    paddingLeft: 0,
    paddingRight: 0,
    borderWidth: 0,
    borderLeftWidth: control.borderWidth,
    borderBottomWidth: control.borderWidth,
    borderTopWidth: control.borderWidth,
    borderTopLeftRadius: first ? control.borderRadius : 0,
    borderTopRightRadius: last ? control.borderRadius : 0,
    borderRightWidth: last ? control.borderWidth : 0,
  }),
  {
    focus: ({theme: {control}}) => ({
      zIndex: Z_INDEXES.zIndexMax,
      borderRadius: control.borderRadius,
    }),
  }
);

// @ts-expect-error - TS7031 - Binding element 'controls' implicitly has an 'any' type. | TS7031 - Binding element 'onClick' implicitly has an 'any' type.
const Controls = ({controls, onClick}) =>
  controls.length ? (
    <FlexRow align="center" justify="end">
      {/* @ts-expect-error - TS7006 - Parameter 'control' implicitly has an 'any' type. | TS7006 - Parameter 'i' implicitly has an 'any' type. */}
      {controls.map((control, i) => (
        <TooltipTrigger
          alternate
          content={
            control.disabled
              ? "New custom animations can't be created while on a branch"
              : ''
          }
          placement="top"
          key={control.key}
        >
          <Hitarea aria-label={control.ariaLabel}>
            <StyledButton
              first={i === 0}
              last={i === controls.length - 1}
              onClick={onClick(control)}
              disabled={control.disabled}
            >
              {control.icon}
            </StyledButton>
          </Hitarea>
        </TooltipTrigger>
      ))}
    </FlexRow>
  ) : null;

export type ItemType = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  extra: any;
  disabled?: boolean;
};

type ControlType = {
  key: string;
  ariaLabel?: string;
  icon: React.ReactNode;
  disabled?: boolean;
};

type MapPropsType = (
  item: ItemType,
  index: number,
  items: Array<ItemType>
) => {
  key?: string;
  label?: string;
  icon?: React.ReactNode;
  extra?: any;
  first?: boolean;
  last?: boolean;
  selected?: boolean;
};

type SharedPropListType = {
  controls: Array<ControlType>;
  items: Array<ItemType>;
  emptyMessage?: string;
  listLabel?: string;
  itemHover?: string;
  extraOpen?: boolean;
  mapProps?: MapPropsType;
};

type InternalPropListType = SharedPropListType & {
  onControlClick: (control: ControlType) => () => void;
  onClick: (item: ItemType) => () => void;
  onRightClick: (item: ItemType) => () => void;
  onDoubleClick: (item: ItemType) => () => void;
  onMouseEnter: (itemKey: string) => () => void;
  onMouseOver: (itemKey: string) => () => void;
  onMouseLeave: (itemKey: string) => void;
  onExtraOpen: (key: string) => () => void;
  onExtraClose: () => void;
  itemHover: string | null;
  extraOpen: boolean | null;
};

const state = syncPropsToState({
  itemHover: {
    updaterName: 'setItemHover',
    initialValue: ({itemHover = null}) => itemHover,
  },
  extraOpen: {
    updaterName: 'setExtraOpen',
    initialValue: ({extraOpen = null}) => extraOpen,
  },
});

const handlers = withHandlers({
  onClick:
    ({onClick = noop}) =>
    (items: any) =>
    () => {
      onClick(items);
    },
  onDoubleClick:
    ({onDoubleClick = noop}) =>
    (items: any) =>
    () => {
      onDoubleClick(items);
    },
  onRightClick:
    // @ts-expect-error - TS7031 - Binding element 'setExtraOpen' implicitly has an 'any' type. | TS7031 - Binding element 'itemHover' implicitly has an 'any' type. | TS7031 - Binding element 'extraOpen' implicitly has an 'any' type.

    ({setExtraOpen, onRightClick = noop, itemHover, extraOpen}) =>
      (item: any) =>
      (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (itemHover === item.key && extraOpen !== item.key) {
          setExtraOpen(item.key);
          onRightClick && onRightClick(item);
        }
      },
  onControlClick:
    ({onControlClick = noop}) =>
    (control: any) =>
    () => {
      onControlClick(control);
    },
  onMouseEnter:
    // @ts-expect-error - TS7031 - Binding element 'setItemHover' implicitly has an 'any' type.

    ({setItemHover}) =>
      (itemKey: any) =>
      () => {
        setItemHover(itemKey);
      },
  onMouseOver:
    // @ts-expect-error - TS7031 - Binding element 'setItemHover' implicitly has an 'any' type.

    ({setItemHover}) =>
      (itemKey: any) =>
      () => {
        setItemHover(itemKey);
      },
  onMouseLeave:
    // @ts-expect-error - TS7031 - Binding element 'setItemHover' implicitly has an 'any' type. | TS7031 - Binding element 'extraOpen' implicitly has an 'any' type.

    ({setItemHover, extraOpen}) =>
      (itemKey: any) =>
      () => {
        extraOpen !== itemKey && setItemHover('');
      },
  onExtraOpen:
    // @ts-expect-error - TS7031 - Binding element 'setExtraOpen' implicitly has an 'any' type.

    ({setExtraOpen}) =>
      (itemKey: any) =>
      () => {
        setExtraOpen(itemKey);
      },
  onExtraClose:
    // @ts-expect-error - TS7031 - Binding element 'setExtraOpen' implicitly has an 'any' type. | TS7031 - Binding element 'setItemHover' implicitly has an 'any' type.

    ({setExtraOpen, setItemHover}) =>
      () => {
        setItemHover(null);
        setExtraOpen(null);
      },
});

const InternalPropList = ({
  controls = emptyArray,
  items: allItems = emptyArray,
  emptyMessage = 'None',
  listLabel,
  mapProps = identity,
  itemHover,
  extraOpen,
  onExtraOpen,
  onExtraClose,
  onControlClick,
  onClick,
  onRightClick,
  onDoubleClick,
  onMouseOver,
  onMouseEnter,
  onMouseLeave,
}: InternalPropListType) => {
  // eslint-disable-next-line no-restricted-syntax -- TODO: Please reuse an existing selector here or define a new one
  const {baseActionListIds} = useDesignerSelector(({IX2UiStore}) => IX2UiStore);
  const items = allItems.map((item) => ({
    ...item,
    disabled: baseActionListIds.has(item.key),
  }));

  return (
    <FlexColumn shrink={0} aria-label={listLabel}>
      <FlexRow justify={listLabel ? 'between' : 'end'}>
        {listLabel && <FinePrint>{listLabel}</FinePrint>}
        <Controls onClick={onControlClick} controls={controls} />
      </FlexRow>

      {items.length === 0 ? (
        <EmptyListItem
          align="center"
          justify="center"
          hasControls={controls.length}
        >
          <FinePrint dim>{emptyMessage}</FinePrint>
        </EmptyListItem>
      ) : (
        items.map((item, index) => (
          <Hitarea key={item.key}>
            <ListItem
              hasControls={controls.length}
              icon={item.icon}
              label={item.label}
              extra={
                itemHover === item.key
                  ? React.cloneElement(item.extra, {
                      onExtraClose,
                      onExtraOpen: onExtraOpen(item.key),
                      extraOpen:
                        // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'boolean' and 'string' have no overlap.
                        extraOpen === item.key &&
                        itemHover === item.key &&
                        !item.disabled,
                    })
                  : null
              }
              onMouseLeave={onMouseLeave(item.key)}
              onMouseOver={onMouseOver(item.key)}
              onMouseEnter={onMouseEnter(item.key)}
              onClick={onClick(item)}
              onDoubleClick={item.disabled ? noop : onDoubleClick(item)}
              onRightClick={item.disabled ? noop : onRightClick(item)}
              {...mapProps(item, index, items)}
            />
          </Hitarea>
        ))
      )}
    </FlexColumn>
  );
};

type PropListType = SharedPropListType & {
  onControlClick?: (control: ControlType) => void;
  onClick?: (item: ItemType) => void;
  onRightClick?: (item: ItemType) => void;
  onDoubleClick?: (item: ItemType) => void;
};

const PropList: React.ComponentType<PropListType> = compose(
  state,
  handlers
)(InternalPropList);

export default PropList;
